<template>
    <div>
        <div v-if="loading" class="text-center" style="padding: 40px 0;">
            <font-awesome-icon :icon="['fas', 'spinner']" spin size="2x" />
        </div>
        <div v-else>
            <div class="row">
                <div class="col-md-5">
                    <div class="card w-100 margin-botton-20 border-0" style="width: 18rem;">
                        <div class="card-body border-primary">
                            <h3 class="h5 card-title">Moon</h3>
                            <p class="card-text">
                                <MoonGraph
                                    :illumination="moonIllumination.illumination"
                                    :moon-phase-name="moonIllumination.moon_phase_name"
                                    :altitude="moonAltitudeAzimuth.altitude != null ? moonAltitudeAzimuth.altitude : null"
                                    :azimuth="moonAltitudeAzimuth.azimuth != null ? moonAltitudeAzimuth.azimuth : null"
                                    :lat="latitude"
                                />
                            </p>
                            <p class="card-text">
                                {{ moonIllumination.formatted_illumination }}
                                <br>{{ moonIllumination.moon_phase_name }}
                            </p>
                        </div>
                    </div>
                </div>
                <div class="col-md-7">
                    <div class="row margin-botton-20" style="margin-top: 20px;">
                        <div class="col-md-12">
                            <div class="row themed">
                                <div class="col-md-6">Moon Direction:</div>
                                <div class="col-md-6">
                                    {{ moonAltitudeAzimuth.formatted_azimuth }}
                                    <span :style="rotateArrow(moonAltitudeAzimuth.azimuth)">{{ arrow }}</span>
                                </div>
                            </div>
                            <div class="row themed">
                                <div class="col-md-6">Moon Altitude:</div>
                                <div class="col-md-6">{{ moonAltitudeAzimuth.altitude }}&deg;</div>
                            </div>
                            <div class="row themed">
                                <div class="col-md-6">Moon Distance:</div>
                                <div class="col-md-6">{{ moonDistance.formatted_distance_km }} Km</div>
                            </div>
                            <div class="row themed">
                                <div class="col-md-6">When:</div>
                                <div class="col-md-6">
                                    <span v-if="useNoon">Noon</span><a v-else href="#" @click.prevent="setNoon">Noon</a>
                                    &nbsp;/&nbsp;
                                    <a v-if="useNoon" href="#" @click.prevent="setNow">Now</a><span v-else>Now</span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <div class="row">
                <div class="col-md-6" v-for="event in moonrisesMoonsets" :key="event.id">
                    <div class="card w-100 margin-botton-20" style="width: 18rem;">
                        <div class="card-body event-card-bg">
                            <h3 class="h5 card-title">{{ event.name }}</h3>
                            <p class="symbol-display">{{ event.symbol }}</p>
                            <p class="card-text">{{ event.naturalday }} at {{ event.time }}</p>
                            <p class="card-text small">{{ event.naturaltime }}</p>
                        </div>
                    </div>
                </div>
            </div>
            <div class="row mt-3" v-if="moonTransitAltitude !== null">
                <div class="col-12">
                    <div class="card w-100 margin-botton-20">
                        <div class="card-body event-card-bg">
                            <MoonAltitudeChart
                                :moonrise="(moonrisesMoonsets.find(e => e.name === 'Moonrise') || {}).time || ''"
                                :moonset="(moonrisesMoonsets.find(e => e.name === 'Moonset') || {}).time || ''"
                                :transit-alt="moonTransitAltitude"
                                :current-time="useNoon ? null : time"
                                title="Moonrise &amp; Moonset"
                                :subtitle="chartSubtitle"
                                :subtitle2="chartSubtitle2"
                            />
                        </div>
                    </div>
                </div>
            </div>
            <div class="row mt-3" v-if="moonriseMonthlyRows.length">
                <div class="col-12">
                    <div class="card w-100 margin-botton-20">
                        <div class="card-body event-card-bg">
                            <h3 class="h4 card-title mb-3">{{ currentMonthLabel }}</h3>
                            <div class="table-responsive">
                            <table class="table table-sm mb-0">
                                <thead>
                                    <tr>
                                        <th>Day</th>
                                        <th>Moonrise</th>
                                        <th>Moonset</th>
                                        <th>Duration</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr v-for="row in moonriseMonthlyRows" :key="row.date"
                                        :class="{ 'table-success': date && parseInt(row.date) === date.getDate() }"
                                        :style="date && parseInt(row.date) === date.getDate() ? { '--bs-table-bg': 'rgba(25,135,84,0.15)', '--bs-table-border-color': 'transparent' } : {}">
                                        <td>{{ row.date }}</td>
                                        <td>{{ row.moonrise }}</td>
                                        <td>{{ row.moonset }}</td>
                                        <td>{{ row.duration }} hrs</td>
                                    </tr>
                                </tbody>
                            </table>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import astroTabMixin from '@/mixins/astroTab'
import astra, { BODY } from '@/api/astra'
import MoonGraph from './MoonGraph'
import MoonAltitudeChart from './MoonAltitudeChart'

export default {
    name: 'TabMoonriseMoonset',
    mixins: [astroTabMixin],
    components: { MoonGraph, MoonAltitudeChart },
    props: {
        active: { type: Boolean, default: false },
    },
    data() {
        return {
            loading: true,
            moonrisesMoonsets: [],
            moonrisesMonthly: [],
            moonTransitAltitude: null,
            moonAltitudeAzimuth: {},
            moonDistance: {},
            moonIllumination: {},
            arrow: '↑',
        }
    },
    computed: {
        chartSubtitle() {
            if (!this.city || !this.city.name) return ''
            const parts = [this.city.name]
            if (this.city.region_name) parts.push(this.city.region_name)
            return 'near ' + parts.join(', ')
        },
        chartSubtitle2() {
            if (!this.date) return ''
            return 'for ' + this.date.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })
        },
        moonriseMonthlyRows() {
            const rows = []
            const events = this.moonrisesMonthly
            for (let i = 0; i < events.length - 1; i += 2) {
                const rise = events[i].name === 'Moonrise' ? events[i] : events[i + 1]
                const set  = events[i].name === 'Moonset'  ? events[i] : events[i + 1]
                rows.push({ date: rise.short_date.split('-')[2], moonrise: rise.time, moonset: set.time, duration: rise.duration_hours })
            }
            return rows
        },
        moonPathDateLabel() {
            if (!this.date) return "Today's Moon Path"
            return 'Moon Path — ' + this.date.toLocaleString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' })
        },
    },
    watch: {
        active(val) { if (val) this.load() },
        city()      { if (this.active) this.load() },
        date()      { if (this.active) this.load() },
    },
    mounted() {
        if (this.active) this.load()
    },
    methods: {
        async load() {
            if (!this.latitude || !this.longitude || !this.timezone) return
            if (this.apiLocked) return
            this.loading = true
            this.moonTransitAltitude = null
            await Promise.allSettled([
                astra.fetchMoonRiseSet({ ...this.loc, ...this.dailyRange }).then(r => {
                    this.moonrisesMoonsets = r.data.events
                    const rise = r.data.events.find(e => e.name === 'Moonrise')
                    const set  = r.data.events.find(e => e.name === 'Moonset')
                    if (rise && set) {
                            astra.fetchAltitudeAzimuth({ ...this.loc, date1: this.dailyTimeRange.date1, body: BODY.MOON })
                            .then(r2 => { this.moonTransitAltitude = r2.data.altitude })
                    }
                }),
                astra.fetchMoonRiseSet({ ...this.loc, ...this.monthlyRange }).then(r => { this.moonrisesMonthly = r.data.events }),
                astra.fetchAltitudeAzimuth({ ...this.loc, date1: this.dailyTimeRange.date1, body: BODY.MOON }).then(r => { this.moonAltitudeAzimuth = r.data }),
                astra.fetchBodyDistance({ ...this.loc, date1: this.dailyTimeRange.date1, start: BODY.EARTH, end: BODY.MOON }).then(r => { this.moonDistance = r.data }),
                astra.fetchMoonIllumination({ ...this.loc, date1: this.dailyTimeRange.date1 }).then(r => { this.moonIllumination = r.data }),
            ])
            this.loading = false
        },
    },
}
</script>

<style scoped>
.themed {
    margin: 5px;
    border-bottom: 1px solid rgba(0, 0, 0, 0.125);
    padding-bottom: 10px;
    padding-top: 10px;
}
.margin-botton-20 { margin-bottom: 20px; }
.symbol-display {
    font-size: 2.5rem;
    margin: 0;
    padding: 0;
    line-height: 1.2;
}
.table-responsive td,
.table-responsive th { white-space: nowrap; }
</style>
